import { useQueries, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  Activation,
  BundleVersion,
  PolicyBundle,
  VersionPolicy,
} from '@/shared/api/generated/policy';
import { ageText } from '@/shared/model/age';
import { personName } from '@/shared/model/person';
import { ConfirmDialog } from '@/shared/ui/ConfirmDialog';
import { Modal } from '@/shared/ui/Modal';
import { SkeletonRows } from '@/shared/ui/Skeleton';

import {
  draftPolicyQuery,
  useActivateVersion,
  useDeprecateVersion,
  versionPolicyQuery,
} from '../api/policy';
import { shortHash } from '../model/bundles';
import { orderVersions } from '../model/workspace';
import { ruleSentence } from './policyText';

/* The governing bundle's history: what is running, what can be rolled back to, and who activated
 * what. A timeline rather than a table, because the question is "what changed, and in what order"
 * — and the one action that undoes a bad publish, rolling back, belongs on the version it returns
 * to rather than in a menu somewhere else.
 */

/** How many versions' rules are read for their counts. History grows at human pace, so this is a
 * bound against a pathological bundle rather than a cap a reader will meet. */
const MAX_RULE_LOOKUPS = 12;

type Asking =
  | { readonly kind: 'withdraw'; readonly semver: string }
  | { readonly kind: 'rollback'; readonly semver: string }
  | null;

export interface VersionsPanelProps {
  readonly governing: PolicyBundle;
  readonly versions: readonly BundleVersion[] | undefined;
  readonly versionsFailed: boolean;
  readonly activations: readonly Activation[] | undefined;
  readonly activationsFailed: boolean;
  readonly runningHashes: ReadonlySet<string>;
  readonly runtimeAvailable: boolean;
  readonly policyOn: boolean;
  readonly canPublish: boolean;
  readonly canActivate: boolean;
  readonly canDeprecate: boolean;
  readonly datasetNames: ReadonlyMap<string, string> | undefined;
  readonly onReviewPublish: () => void;
  readonly onEditRules: () => void;
  readonly onFlash: (message: string) => void;
}

export function VersionsPanel({
  governing,
  versions,
  versionsFailed,
  activations,
  activationsFailed,
  runningHashes,
  runtimeAvailable,
  policyOn,
  canPublish,
  canActivate,
  canDeprecate,
  datasetNames,
  onReviewPublish,
  onEditRules,
  onFlash,
}: VersionsPanelProps): ReactElement {
  const { t } = useTranslation();
  const activate = useActivateVersion();
  const deprecate = useDeprecateVersion();
  const [asking, setAsking] = useState<Asking>(null);
  const [viewing, setViewing] = useState<BundleVersion | null>(null);

  const ordered = orderVersions(versions ?? []);
  const lookups = ordered.slice(0, MAX_RULE_LOOKUPS);
  const rules = useQueries({
    queries: lookups.map((version) => ({
      ...(version.semver
        ? versionPolicyQuery(governing.id, version.semver)
        : draftPolicyQuery(governing.id)),
      retry: false,
    })),
  });
  const ruleRead = new Map<BundleVersion, VersionPolicy | undefined>(
    lookups.map((version, index) => [version, rules[index]?.data]),
  );

  const active = ordered.find((version) => version.state === 'active');

  if (versionsFailed) {
    return <p className="lq-pq__note">{t('policy.versions.failed')}</p>;
  }
  if (versions === undefined) {
    return <SkeletonRows rows={3} columns={2} />;
  }

  const runLabel = (
    version: BundleVersion,
  ): { readonly text: string; readonly running: boolean } => {
    if (runningHashes.has(version.content_hash)) {
      return { text: t('policy.versions.running'), running: true };
    }
    if (!runtimeAvailable) return { text: t('policy.versions.noEngine'), running: false };
    if (!policyOn) return { text: t('policy.versions.notConsulted'), running: false };
    return { text: t('policy.versions.loading'), running: false };
  };

  const meta = (version: BundleVersion): string => {
    const read = ruleRead.get(version);
    const parts: string[] = [];
    if (read !== undefined) {
      parts.push(
        read.rules === null
          ? t('policy.versions.compiled')
          : t('policy.versions.ruleCount', { count: read.rules.length }),
      );
    }
    parts.push(personName(version.created_by));
    const age = ageText(t, version.created_at, new Date());
    if (age !== null) parts.push(age);
    return parts.join(' · ');
  };

  const confirm = (): void => {
    if (asking === null) return;
    const args = { bundleId: governing.id, semver: asking.semver };
    if (asking.kind === 'withdraw') {
      deprecate.mutate(args, {
        onSuccess: () => {
          setAsking(null);
          onFlash(t('policy.versions.withdrawn', { semver: args.semver }));
        },
      });
    } else {
      activate.mutate(args, {
        onSuccess: () => {
          setAsking(null);
          onFlash(t('policy.versions.rolledBack', { semver: args.semver }));
        },
      });
    }
  };

  return (
    <div className="lq-pq__split">
      <ol className="lq-pq__timeline" aria-label={t('policy.versions.label')}>
        {ordered.map((version, index) => {
          const isActive = version.state === 'active';
          const run = isActive ? runLabel(version) : null;
          const semver = version.semver ?? null;
          return (
            <li
              key={`${version.content_hash}-${semver ?? 'draft'}`}
              className="lq-pq__stop"
            >
              <span className="lq-pq__rail" aria-hidden="true">
                <span className="lq-pq__dot" data-state={version.state} />
                {index < ordered.length - 1 ? <span className="lq-pq__line" /> : null}
              </span>
              <article
                className="lq-pq__version"
                data-state={version.state}
                aria-label={semver === null ? t('policy.versions.draft') : `v${semver}`}
              >
                <div className="lq-pq__versionHead">
                  <span className="lq-pq__versionTitle">
                    {semver === null ? t('policy.versions.draft') : `v${semver}`}
                  </span>
                  <span className="lq-pq__pill" data-state={version.state}>
                    {t(`policy.state.${version.state}`)}
                  </span>
                  {run !== null ? (
                    <span
                      className="lq-pq__run"
                      data-running={run.running ? 'true' : 'false'}
                    >
                      <span className="lq-pq__runDot" aria-hidden="true" />
                      {run.text}
                    </span>
                  ) : null}
                </div>
                <p className="lq-pq__versionMeta">
                  {meta(version)}
                  {' · '}
                  <span className="lq-pq__mono" title={version.content_hash}>
                    {shortHash(version.content_hash)}
                  </span>
                </p>
                <div className="lq-pq__versionActs">
                  {version.state === 'draft' ? (
                    <>
                      <button
                        type="button"
                        className="lq-pq__btn lq-pq__btn--go"
                        disabled={!canPublish}
                        title={canPublish ? undefined : t('policy.versions.noPermission')}
                        onClick={onReviewPublish}
                      >
                        {t('policy.publish.open')}
                      </button>
                      <button type="button" className="lq-pq__btn" onClick={onEditRules}>
                        {t('policy.versions.editRules')}
                      </button>
                    </>
                  ) : null}
                  {version.state === 'deprecated' && semver !== null ? (
                    <button
                      type="button"
                      className="lq-pq__btn"
                      disabled={!canActivate}
                      title={canActivate ? undefined : t('policy.versions.noPermission')}
                      onClick={() => {
                        setAsking({ kind: 'rollback', semver });
                      }}
                    >
                      {t('policy.versions.rollBack')}
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="lq-pq__btn"
                    onClick={() => {
                      setViewing(version);
                    }}
                  >
                    {t('policy.versions.view')}
                  </button>
                  {isActive && semver !== null ? (
                    <button
                      type="button"
                      className="lq-pq__btn lq-pq__btn--alarm"
                      disabled={!canDeprecate}
                      title={canDeprecate ? undefined : t('policy.versions.noPermission')}
                      onClick={() => {
                        setAsking({ kind: 'withdraw', semver });
                      }}
                    >
                      {t('policy.versions.withdraw')}
                    </button>
                  ) : null}
                </div>
              </article>
            </li>
          );
        })}
        {ordered.length === 0 ? (
          <li className="lq-pq__emptyRules">{t('policy.versions.none')}</li>
        ) : null}
      </ol>

      <aside className="lq-pq__aside lq-pq__history" aria-labelledby="pq-history-title">
        <h3 id="pq-history-title" className="lq-pq__asideTitle">
          {t('policy.activations.title')}
        </h3>
        {activationsFailed ? (
          <p className="lq-pq__asideSub">{t('policy.activations.failed')}</p>
        ) : activations === undefined ? (
          <SkeletonRows rows={2} columns={1} />
        ) : activations.length === 0 ? (
          <p className="lq-pq__asideSub">{t('policy.activations.none')}</p>
        ) : (
          <ol className="lq-pq__acts">
            {activations.map((entry) => (
              <li key={`${entry.semver}-${entry.activated_at}`} className="lq-pq__actRow">
                <div>
                  <p className="lq-pq__actTitle">
                    {t('policy.activations.activated', { semver: entry.semver })}
                  </p>
                  <p className="lq-pq__actSub">
                    {entry.previous_semver
                      ? t('policy.activations.replaced', {
                          semver: entry.previous_semver,
                          person: personName(entry.actor),
                        })
                      : t('policy.activations.first', {
                          person: personName(entry.actor),
                        })}
                  </p>
                </div>
                <span className="lq-pq__actWhen">
                  {ageText(t, entry.activated_at, new Date())}
                </span>
              </li>
            ))}
          </ol>
        )}
      </aside>

      <ConfirmDialog
        open={asking !== null}
        title={
          asking?.kind === 'withdraw'
            ? t('policy.versions.withdrawTitle', { semver: asking.semver })
            : t('policy.versions.rollBackTitle', { semver: asking?.semver ?? '' })
        }
        message={
          asking?.kind === 'withdraw'
            ? policyOn
              ? t('policy.versions.withdrawOn')
              : t('policy.versions.withdrawOff')
            : active?.semver
              ? t('policy.versions.rollBackBody', {
                  semver: asking?.semver ?? '',
                  current: active.semver,
                })
              : t('policy.versions.rollBackBodyNone', { semver: asking?.semver ?? '' })
        }
        confirmLabel={
          asking?.kind === 'withdraw'
            ? t('policy.versions.withdraw')
            : t('policy.versions.rollBackDo', { semver: asking?.semver ?? '' })
        }
        cancelLabel={t('common.cancel')}
        tone={asking?.kind === 'withdraw' && policyOn ? 'danger' : 'primary'}
        busy={activate.isPending || deprecate.isPending}
        error={
          activate.isError
            ? activate.error.message
            : deprecate.isError
              ? deprecate.error.message
              : undefined
        }
        onConfirm={confirm}
        onCancel={() => {
          setAsking(null);
          activate.reset();
          deprecate.reset();
        }}
      />

      <ViewVersionDialog
        bundle={governing}
        version={viewing}
        datasetNames={datasetNames}
        onClose={() => {
          setViewing(null);
        }}
      />
    </div>
  );
}

interface ViewVersionDialogProps {
  readonly bundle: PolicyBundle;
  readonly version: BundleVersion | null;
  readonly datasetNames: ReadonlyMap<string, string> | undefined;
  readonly onClose: () => void;
}

/** One version's rules as sentences, with the compiled policy a click away for whoever is
 * diagnosing an unexpected decision. Nothing in the normal workflow needs the Rego. */
function ViewVersionDialog({
  bundle,
  version,
  datasetNames,
  onClose,
}: ViewVersionDialogProps): ReactElement {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'rules' | 'rego'>('rules');
  const semver = version?.semver ?? null;
  /* Its own read, on the timeline's cache key, so a version past the counted ones still opens. */
  const query = useQuery({
    ...(semver === null
      ? draftPolicyQuery(bundle.id)
      : versionPolicyQuery(bundle.id, semver)),
    enabled: version !== null,
    retry: false,
  });
  const read = query.data;

  return (
    <Modal
      open={version !== null}
      title={
        semver === null
          ? t('policy.view.titleDraft', { bundle: bundle.name })
          : t('policy.view.title', { bundle: bundle.name, semver })
      }
      size="wide"
      onClose={() => {
        setTab('rules');
        onClose();
      }}
    >
      <div
        className="lq-pq__seg lq-pq__seg--small"
        role="group"
        aria-label={t('policy.view.label')}
      >
        <button
          type="button"
          className="lq-pq__segOption"
          aria-pressed={tab === 'rules'}
          onClick={() => {
            setTab('rules');
          }}
        >
          {t('policy.view.rules')}
        </button>
        <button
          type="button"
          className="lq-pq__segOption"
          aria-pressed={tab === 'rego'}
          onClick={() => {
            setTab('rego');
          }}
        >
          {t('policy.view.rego')}
        </button>
      </div>
      {query.isError ? (
        <p className="lq-pq__note">{t('policy.view.failed')}</p>
      ) : read === undefined ? (
        <SkeletonRows rows={3} columns={1} />
      ) : read.rules === null ? (
        <p className="lq-pq__note">{t('policy.view.compiled')}</p>
      ) : tab === 'rules' ? (
        <ol className="lq-pq__viewList">
          {read.rules.map((rule, index) => (
            <li
              key={`${String(index)}-${rule.role}-${rule.action}`}
              className="lq-pq__viewRule"
            >
              <span className="lq-pq__viewN">{String(index + 1)}</span>
              <span className="lq-pq__viewTag" data-effect={rule.effect}>
                {rule.effect === 'deny'
                  ? t('policy.rules.deny')
                  : t('policy.rules.allow')}
              </span>
              <span>{ruleSentence(t, rule, datasetNames)}</span>
            </li>
          ))}
          {read.rules.length === 0 ? (
            <li className="lq-pq__note">{t('policy.view.empty')}</li>
          ) : null}
        </ol>
      ) : (
        <pre className="lq-pq__rego">{read.rego ?? t('policy.view.noRego')}</pre>
      )}
    </Modal>
  );
}
